import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  CreateOperationChainItemMutationRequest,
  CreateOperationChainItemMutationResponse,
} from "../types/CreateOperationChainItem"

type CreateOperationChainItemClient = typeof client<
  CreateOperationChainItemMutationResponse,
  Error,
  CreateOperationChainItemMutationRequest
>
type CreateOperationChainItem = {
  data: CreateOperationChainItemMutationResponse
  error: Error
  request: CreateOperationChainItemMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<CreateOperationChainItemClient>>
  client: {
    parameters: Partial<Parameters<CreateOperationChainItemClient>[0]>
    return: Awaited<ReturnType<CreateOperationChainItemClient>>
  }
}
/**
 * @summary Create an operation chain item
 * @link /v1/operation-chain/create-operation-chain-item
 */
export function useCreateOperationChainItem(
  options: {
    mutation?: UseMutationOptions<
      CreateOperationChainItem["response"],
      CreateOperationChainItem["error"],
      CreateOperationChainItem["request"]
    >
    client?: CreateOperationChainItem["client"]["parameters"]
  } = {}
): UseMutationResult<
  CreateOperationChainItem["response"],
  CreateOperationChainItem["error"],
  CreateOperationChainItem["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    CreateOperationChainItem["response"],
    CreateOperationChainItem["error"],
    CreateOperationChainItem["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        CreateOperationChainItem["data"],
        CreateOperationChainItem["error"],
        CreateOperationChainItem["request"]
      >({
        method: "post",
        url: `/v1/operation-chain/create-operation-chain-item`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
